import { TTierCondition } from '@modules/admin/Tools/LenderUtility/interfaces';
import { IEligibleLender } from '../../../../models/EligibleLenderModel';
import { ITierCondition } from '../../../../models/TierConditionModel';

export function PMT(
  rate: number,
  nperiod: number,
  principalValue: number,
  firstPaymentDays?: number,
  finalValue?: number,
  type?: number,
): number {
  let extraPrincipal = 0;
  const dailyRate = rate / 30;
  if (!finalValue) finalValue = 0;
  if (!type) type = 0;

  if (rate === 0) return -(principalValue + finalValue) / nperiod;
  const pvif = Math.pow(1 + rate, nperiod);

  if (firstPaymentDays && firstPaymentDays > 30) {
    const extraDays = firstPaymentDays - 30;
    extraPrincipal = principalValue * dailyRate * extraDays;
    principalValue += extraPrincipal;
  }

  let pmt = (rate / (pvif - 1)) * -(principalValue * pvif + finalValue);

  if (type === 1) {
    pmt /= 1 + rate;
  }

  return pmt;
}

interface IDailyAmortization {
  periodNumber: number;
  periodStartDate: Date;
  daysSinceLastPayment: number;
  dailyInterest: number;
  interestThisPeriod: number;
  principalThisPeriod: number;
  newBalance: number;
  paymentAmount: number;
}

interface IDailyCalculations {
  apr: boolean | null;
  firstPaymentAmount: number;
  lastPaymentAmount: number;
  amortizationTable: number | null;
  interestAmount: number;
  totalAmount: number;
}

export function dailyCalculations(
  principal: string | number,
  rate: number,
  term: number,
  contractDateParam: string,
  firstPaymentDateParam: string,
): IDailyCalculations | undefined {
  //Sometimes dates come in as string, turn them into date objects here
  const contractDate = new Date(contractDateParam);
  const firstPaymentDate = new Date(firstPaymentDateParam);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dayDiff = (date1: any, date2: any): number => {
    //TODO dateDiff in documentation, how it works
    const diffTime = Math.abs(date2 - date1);

    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  let lastPaymentAmount = 0;
  const dailyInterest = Number(((rate / 365) * (principal as number)).toFixed(4));
  const firstPaymentDateForOddInterest = new Date(firstPaymentDate);
  const daysForOddInterest =
    dayDiff(
      contractDate,
      new Date(firstPaymentDateForOddInterest.setMonth(firstPaymentDateForOddInterest.getMonth() - 1)),
    ) + 1;
  const interestToAdd = (dailyInterest * daysForOddInterest).toFixed(2);
  const balanceForPayment = (parseFloat(principal as string) + parseFloat(interestToAdd)).toFixed(4);
  const rateForCalculation = Math.pow(1 + rate / 365, 365.0 / 12.0) - 1;
  const paymentAmount = Number(PMT(rateForCalculation, term, -balanceForPayment).toFixed(2));

  const dailyAmortization: IDailyAmortization[] = [];
  for (let i = 0; i < term; i++) {
    if (i === 0) {
      //First payment
      const daysSinceLastPayment = dayDiff(contractDate, firstPaymentDate);
      const interestThisPeriod = Number((daysSinceLastPayment * dailyInterest).toFixed(2));
      const principalThisPeriod = Number((paymentAmount - interestThisPeriod).toFixed(2));
      const newBalance = Number(((principal as number) - principalThisPeriod).toFixed(2));
      dailyAmortization.push({
        periodNumber: i + 1,
        periodStartDate: firstPaymentDate,
        daysSinceLastPayment: daysSinceLastPayment,
        dailyInterest: dailyInterest,
        interestThisPeriod: interestThisPeriod,
        principalThisPeriod: principalThisPeriod,
        newBalance: newBalance,
        paymentAmount: Number((parseFloat(interestThisPeriod + '') + parseFloat(principalThisPeriod + '')).toFixed(2)),
      });
    } else if (i < term - 1) {
      //Middle payments
      const previousPeriodAmortization2 = dailyAmortization[i - 1];
      const previousPeriodStartDate2 = new Date(previousPeriodAmortization2.periodStartDate.getTime());
      const periodStartDate2 = new Date(previousPeriodStartDate2.setMonth(previousPeriodStartDate2.getMonth() + 1));
      const daysSinceLastPayment2 = dayDiff(previousPeriodAmortization2.periodStartDate, periodStartDate2);
      const dailyInterestThisPeriod2 = Number(((previousPeriodAmortization2.newBalance * rate) / 365).toFixed(14));
      const interestThisPeriod2 = Number((daysSinceLastPayment2 * dailyInterestThisPeriod2).toFixed(2));
      const principalThisPeriod2 = Number((paymentAmount - interestThisPeriod2).toFixed(2));
      const newBalance2 = Number((previousPeriodAmortization2.newBalance - principalThisPeriod2).toFixed(2));
      dailyAmortization.push({
        periodNumber: i + 1,
        periodStartDate: periodStartDate2,
        daysSinceLastPayment: daysSinceLastPayment2,
        dailyInterest: dailyInterestThisPeriod2,
        interestThisPeriod: interestThisPeriod2,
        principalThisPeriod: principalThisPeriod2,
        newBalance: newBalance2,
        paymentAmount: Number(
          (parseFloat(interestThisPeriod2 + '') + parseFloat(principalThisPeriod2 + '')).toFixed(2),
        ),
      });
    } else {
      //Last payment
      const previousPeriodAmortization3 = dailyAmortization[i - 1];
      const previousPeriodStartDate3 = new Date(previousPeriodAmortization3.periodStartDate.getTime());
      const periodStartDate3 = new Date(previousPeriodStartDate3.setMonth(previousPeriodStartDate3.getMonth() + 1));
      const daysSinceLastPayment3 = dayDiff(previousPeriodAmortization3.periodStartDate, periodStartDate3);
      const dailyInterestThisPeriod3 = Number(((previousPeriodAmortization3.newBalance * rate) / 365).toFixed(14));
      const interestThisPeriod3 = Number((daysSinceLastPayment3 * dailyInterestThisPeriod3).toFixed(2));
      const principalThisPeriod3 = Number((paymentAmount - interestThisPeriod3).toFixed(2));
      const newBalance3 = Number((previousPeriodAmortization3.newBalance - principalThisPeriod3).toFixed(2));
      lastPaymentAmount = Number(
        (
          parseFloat(interestThisPeriod3 + '') +
          parseFloat(principalThisPeriod3 + '') +
          parseFloat(newBalance3 + '')
        ).toFixed(2),
      ); //The balance on the last period is negative making last payment amount different
      dailyAmortization.push({
        periodNumber: i + 1,
        periodStartDate: periodStartDate3,
        daysSinceLastPayment: daysSinceLastPayment3,
        dailyInterest: dailyInterestThisPeriod3,
        interestThisPeriod: interestThisPeriod3,
        principalThisPeriod: principalThisPeriod3,
        newBalance: newBalance3,
        paymentAmount: lastPaymentAmount,
      });
    }
    let interestAmount = 0;
    dailyAmortization.forEach((value: IDailyAmortization) => {
      interestAmount += parseFloat(value.interestThisPeriod + '');
    });

    return {
      apr: null,
      firstPaymentAmount: paymentAmount,
      lastPaymentAmount: lastPaymentAmount,
      amortizationTable: null,
      interestAmount: interestAmount,
      totalAmount: +principal + interestAmount,
    };
  }
}

export const getDti = (
  eligibleLenders: IEligibleLender,
  monthlyIncome: number,
): { maxDtiGreen: number; maxDtiYellow: number } => {
  let maxDtiGreen = 0;
  let maxDtiYellow = 0;
  const ficoValue = eligibleLenders.primaryBorrowerBureau?.isPreferredScoreSelected ? eligibleLenders.primaryBorrowerBureau?.preferredScore || 0 : eligibleLenders.primaryBorrowerBureau?.defaultScore || 0;

  if (eligibleLenders.code.toLocaleLowerCase() === 'ki') {
    if (ficoValue >= 700) {
      maxDtiGreen = 60;
      maxDtiYellow = 60;
    } else {
      maxDtiGreen = 50;
      maxDtiYellow = 50;
    }
  } else if (eligibleLenders.code.toLocaleLowerCase() === 'az') {
    if (ficoValue >= 720) {
      if (monthlyIncome * 12 <= 40000) {
        maxDtiGreen = 45;
        maxDtiYellow = 45;
      } else if (monthlyIncome * 12 <= 80000) {
        maxDtiGreen = 50;
        maxDtiYellow = 50;
      } else if (monthlyIncome * 12 > 80000) {
        maxDtiGreen = 55;
        maxDtiYellow = 55;
      }
    } else if (ficoValue >= 660) {
      if (monthlyIncome * 12 <= 40000) {
        maxDtiGreen = 40;
        maxDtiYellow = 40;
      } else if (monthlyIncome * 12 <= 80000) {
        maxDtiGreen = 45;
        maxDtiYellow = 45;
      } else if (monthlyIncome * 12 > 80000) {
        maxDtiGreen = 50;
        maxDtiYellow = 50;
      }
    } else {
      if (monthlyIncome * 12 <= 40000) {
        maxDtiGreen = 35;
        maxDtiYellow = 35;
      } else if (monthlyIncome * 12 <= 80000) {
        maxDtiGreen = 40;
        maxDtiYellow = 40;
      } else if (monthlyIncome * 12 > 80000) {
        maxDtiGreen = 45;
        maxDtiYellow = 45;
      }
    }
  } else if (eligibleLenders.code.toLocaleLowerCase() === 'mca') {
    maxDtiGreen = eligibleLenders.tierConditions[0].maxDti || 0;
    maxDtiYellow = maxDtiGreen;
  } else {
    maxDtiGreen = eligibleLenders.maxDtiGreen || 0;
    maxDtiYellow = eligibleLenders.maxDtiYellow || 0;
  }

  return { maxDtiYellow, maxDtiGreen };
};

export const getPti = (
  eligibleLenders: IEligibleLender,
): { maxPtiGreen: number; maxPtiYellow: number } => {
  let maxPtiGreen = 0;
  let maxPtiYellow = 0;
  const ficoValue = eligibleLenders.primaryBorrowerBureau?.defaultScore || 0;

  if (eligibleLenders.code.toLocaleLowerCase() === 'ki') {
    if (ficoValue >= 700) {
      maxPtiGreen = 20;
      maxPtiYellow = 20;
    } else {
      maxPtiGreen = 15;
      maxPtiYellow = 15;
    }
  } else if (eligibleLenders.code.toLocaleLowerCase() === 'mca') {
    maxPtiGreen = eligibleLenders.tierConditions[0].maxPti || 0;
    maxPtiYellow = maxPtiGreen;
  } else {
    maxPtiGreen = eligibleLenders.maxPtiGreen || 0;
    maxPtiYellow = eligibleLenders.maxPtiYellow || 0;
  }

  return { maxPtiYellow, maxPtiGreen };
};

export const tierConditionByMaxLtvGreen = (lender: IEligibleLender, LTV: number) => {
  let result: Partial<ITierCondition> | undefined;
  const localTerm = lender.inputs?.term || 0;
  const properTermGroup = lender?.tierConditions.find((item: TTierCondition) => (item.termGroup || 0) >= localTerm);
  const tierConditionsTermGroup = lender?.tierConditions.filter(
    (item: TTierCondition) => item.termGroup === properTermGroup?.termGroup,
  );

  const tierConditionsRateAsc = tierConditionsTermGroup.sort((a: TTierCondition, b: TTierCondition) => {
    return (a.rate || 0) - (b.rate || 0);
  });

  result = tierConditionsRateAsc?.find((item: Partial<ITierCondition>) => (item.maxLtvGreen || 0) >= LTV);

  if (!result) {
    const tierConditionsMaxLtvGreenDesc = tierConditionsTermGroup.sort((a: TTierCondition, b: TTierCondition) => {
      return (b.maxLtvGreen || 0) - (a.maxLtvGreen || 0);
    });

    result = tierConditionsMaxLtvGreenDesc?.find((item: Partial<ITierCondition>) => (item.maxLtvGreen || 0) < LTV);
  }

  return result;
};

export const tierConditionByTerm = (lender: IEligibleLender, LTV: number, cashOut: number, localTerm: number) => {
  let tierConditions = lender.tierConditions.filter((item: TTierCondition) => ((item.termGroup || 0) >= localTerm));

  if (cashOut > 0) {
    // in case of cashout give precedence to tier conditonn for cashout 
    tierConditions = tierConditions.filter((item: TTierCondition) => item.type === 'Cash-out');
  }
  // if no tier condtions from above if clause then use he tierConditon with null or empty values
  if (!tierConditions.length) {
    tierConditions = tierConditions.filter((item: TTierCondition) => !item.type);
  }

  // fiter by LTV and sort by asc
  let results = tierConditions.filter((item: TTierCondition) => (item.maxLtvGreen || 0) >= LTV);

  if (results?.length) {
    results = results.sort(
      (a: TTierCondition, b: TTierCondition) => {
        return (a.termGroup || 0) - (b.termGroup || 0);
      },
    );
  } else {
    // reverse LTV conditon during filter of tierConditions and sort by desc
    results = tierConditions.filter((item: TTierCondition) => (item.maxLtvGreen || 0) <= LTV);
    results = results.sort(
      (a: TTierCondition, b: TTierCondition) => {
        return (b.termGroup || 0) - (a.termGroup || 0);
      },
    );
  }

  return results[0];
};

export const maxLtvGreen = (lender: IEligibleLender, localTerm: number): number => {
  let res: TTierCondition | undefined = lender?.tierConditions
    .sort((a: TTierCondition, b: TTierCondition) => {
      return (b.maxLtvGreen || 0) - (a.maxLtvGreen || 0);
    })
    .find((item: TTierCondition) => item.termGroup === localTerm);

  if (!res) {
    const termGroupAsc: Partial<ITierCondition>[] = lender?.tierConditions.sort(
      (a: TTierCondition, b: TTierCondition) => {
        return (a.termGroup || 0) - (b.termGroup || 0);
      },
    );
    const properTermGroup = termGroupAsc.find((item: TTierCondition) => (item.termGroup || 0) >= localTerm);

    const tierConditionsTermGroup = lender?.tierConditions.filter(
      (item: TTierCondition) => item.termGroup === properTermGroup?.termGroup,
    );

    const maxLtvGreenListDesc = tierConditionsTermGroup.sort((a: TTierCondition, b: TTierCondition) => {
      return (b.maxLtvGreen || 0) - (a.maxLtvGreen || 0);
    });

    res = maxLtvGreenListDesc[0];
  }
  return res?.maxLtvGreen || 0;
};

export const addDays = (date: string = new Date().toString(), days = 0) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const getPayment = (pmt: number, totalAmountFinanced: number, rate: number) => {
  const currentPmt = (-1 * Math.log(1 - (totalAmountFinanced / pmt) * (rate / 12))) / Math.log(1 + rate / 12);
  return isNaN(currentPmt) || currentPmt < 0 ? 0 : currentPmt.toFixed();
};
